import React, { useEffect, useMemo, useState } from 'react'
import { tidyDisplayTitle } from 'src/utils/venueName'
import styled from 'styled-components'
import { Card, Table, Button, Tag, Avatar, Checkbox, Tooltip } from 'antd'
import { Modal } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import allergenEventEmitter from 'src/utils/allergenEventEmitter'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import {
  computeCustomIngredientLineCost,
  computeIngredientLineCost,
  extractAndCalculateSize,
  findVenueProductForIngredient,
  resolveIngredientUnitPrice,
} from 'src/utils/priceFunctions'
import {
  LockOutlined,
  UnlockOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import { calculateSellingPriceInclVatFormatted } from 'src/utils/vatCalculations'
import { formatGbp } from 'src/utils/formatGbp'
import { GP_COLOUR_GUIDE_TEXT } from 'src/utils/recipeGp'
import RecipeGpBadge from './RecipeGpBadge'
import RecipeGpReviewBanner from './RecipeGpReviewBanner'
import {
  isRecipeArchived,
  recipeMonitoringDetailCopy,
} from 'src/utils/recipeMonitoring'
import RecipeEconomicsStaleHint from './RecipeEconomicsStaleHint'
import MissingProductsBanner from './MissingProductsBanner'
import {
  computeRecipeIngredientLineSum,
  recipeEconomicsAreStale,
} from 'src/utils/recipeEconomicsStale'
import {
  buildShoppingListProductIdSet,
  detectMissingRecipeProducts,
} from 'src/utils/menuBuilderMissingProducts'

const MonitoringNote = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.65);
`

const StyledCard = styled(Card)`
  margin-top: 24px;
  transition: box-shadow 220ms ease, border-color 220ms ease;

  ${props =>
    props.$highlighted
      ? `
    box-shadow: 0 0 0 3px rgba(181, 217, 34, 0.35), 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: rgba(181, 217, 34, 0.55);
  `
      : ''}
`

const StyledModal = styled(Modal)`
  .ant-modal-close {
    top: -10px; /* Move the close button upwards */
    right: -10px; /* Move it slightly right */
    background: white;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
  }

  .ant-modal-close:hover {
    background: #f0f0f0;
  }

  .ant-modal-content {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }
`

const RecipeDetails = ({
  recipe,
  handleEdit,
  recipes = [],
  venueId,
  products = [],
  onMissingProductsResolved,
  onMarkReviewed,
}) => {
  const [ingredients, setIngredients] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [allergensEditedByUser, setAllergensEditedByUser] = useState(false)
  const [expandedStack, setExpandedStack] = useState({})
  const [isHighlighting, setIsHighlighting] = useState(false)

  const dispatch = useDispatch()

  const venueProductsFromStore = useSelector(
    state => state.venueProducts?.products || [],
  )

  const recipeById = useMemo(
    () => new Map(recipes.map(r => [r._id, r])),
    [recipes],
  )

  useEffect(() => {
    const allIngredients = [
      ...(recipe.ingredients || []).map(item => ({
        ...item,
        _lineSource: 'oms',
      })),
      ...(recipe.customIngredients || []).map(item => ({
        ...item,
        _lineSource: 'custom',
      })),
    ]
    setIngredients(allIngredients)
    const anyAllergenEdited = allIngredients.some(
      ing => ing?.product?.allergensEditedByUser,
    )
    setAllergensEditedByUser(anyAllergenEdited)
  }, [recipe, recipe.ingredients, recipe.customIngredients])

  const venueProductsCatalog = useMemo(() => {
    const merged = new Map()
    const add = vp => {
      if (!vp) return
      if (vp._id) merged.set(`vp:${String(vp._id)}`, vp)
      if (vp.product?._id) merged.set(`cat:${String(vp.product._id)}`, vp)
    }
    venueProductsFromStore.forEach(add)
    products.forEach(add)
    return [...merged.values()]
  }, [venueProductsFromStore, products])

  const shoppingListProductIds = useMemo(
    () => buildShoppingListProductIdSet(venueProductsCatalog),
    [venueProductsCatalog],
  )

  const missingProducts = useMemo(
    () =>
      detectMissingRecipeProducts(recipe.ingredients || [], {
        shoppingListProductIds,
      }),
    [recipe.ingredients, shoppingListProductIds],
  )

  useEffect(() => {
    if (venueId) {
      dispatch(fetchVenueProducts(venueId))
    }
  }, [venueId, dispatch])

  // Listen for allergen update events to refresh recipe data
  useEffect(() => {
    const handleAllergenUpdate = () => {
      // console.log('RecipeDetails: Allergen update event received:', eventData)
      // Force a re-render by updating the ingredients state
      const allIngredients = [
        ...(recipe.ingredients || []),
        ...(recipe.customIngredients || []),
      ]
      setIngredients(allIngredients)
    }

    allergenEventEmitter.on('allergen-updated', handleAllergenUpdate)

    return () => {
      allergenEventEmitter.off('allergen-updated', handleAllergenUpdate)
    }
  }, [recipe])

  useEffect(() => {
    if (!recipe?._id) return
    setIsHighlighting(true)
    const timerId = setTimeout(() => {
      setIsHighlighting(false)
    }, 1200)
    return () => clearTimeout(timerId)
  }, [recipe?._id])

  const calculatedAllergens = useMemo(
    () => ({
      allergens: Array.isArray(recipe?.allergens) ? recipe.allergens : [],
      allergenTraces: Array.isArray(recipe?.allergenTraces)
        ? recipe.allergenTraces
        : [],
    }),
    [recipe?.allergens, recipe?.allergenTraces],
  )

  // Extract allergens including stacked recipes (recipeIngredients) - fallback for recipe ingredients
  const getAllergens = () => {
    // Use calculated allergens from venue products first - this already includes all allergens
    // from ingredients and nested recipes, so we don't need to manually traverse again
    const calculated = calculatedAllergens.allergens || []
    return calculated
  }

  const getIngredientUnitPrice = record => {
    const venueProduct = findVenueProductForIngredient(
      record,
      venueProductsCatalog,
    )

    return resolveIngredientUnitPrice({
      ingredient: record,
      venueProduct,
      product: record?.product,
    })
  }

  const computeIngredientTotal = record => {
    if (record?._lineSource === 'custom') {
      return computeCustomIngredientLineCost(record)
    }

    const selectedSize =
      record?.size ||
      extractAndCalculateSize(record?.product?.size || '').numbers?.[0] ||
      0
    const price = getIngredientUnitPrice(record)

    return computeIngredientLineCost({
      productSize: record?.product?.size || '',
      unitPrice: price,
      selectedSize,
      UoM: record?.UoM,
      conversion: record?.conversion,
    })
  }

  const displayEconomics = useMemo(() => {
    const lineSum = computeRecipeIngredientLineSum(recipe, {
      recipeById,
      venueProductsCatalog,
    })
    const portions = Number(recipe.portions) || 1
    const computedCostPerPortion = Math.round((lineSum / portions) * 100) / 100
    const persistedCostPerRecipe = Number(recipe.costPerRecipe)
    const persistedCostPerPortion = Number(recipe.costPerPortion)
    const isStale = recipeEconomicsAreStale(persistedCostPerRecipe, lineSum)

    return {
      lineSum,
      costPerRecipe: Number.isFinite(persistedCostPerRecipe)
        ? persistedCostPerRecipe
        : lineSum,
      costPerPortion: Number.isFinite(persistedCostPerPortion)
        ? persistedCostPerPortion
        : computedCostPerPortion,
      isStale,
      linesMatchHeader: !isStale,
    }
  }, [recipe, recipeById, venueProductsCatalog])

  // Allergens helper for a given base recipe
  const getAllergensForBase = baseRecipe => {
    if (!baseRecipe) return []
    const all = new Set()
    const visited = new Set()
    const dfs = rid => {
      if (!rid || visited.has(rid)) return
      visited.add(rid)
      const r = recipeById.get(rid)
      if (!r) return
      ;(r.ingredients || []).forEach(ing => {
        ;(ing?.product?.allergens || []).forEach(a => all.add(a))
      })
      ;(r.recipeIngredients || []).forEach(child => dfs(child.recipeId))
    }
    ;(baseRecipe.ingredients || []).forEach(ing => {
      ;(ing?.product?.allergens || []).forEach(a => all.add(a))
    })
    ;(baseRecipe.recipeIngredients || []).forEach(child => dfs(child.recipeId))
    return Array.from(all)
  }

  const openImageModal = image => {
    setSelectedImage(image)
    setIsModalVisible(true)
  }

  return (
    <StyledCard
      $highlighted={isHighlighting}
      title={tidyDisplayTitle(recipe.title)}
      extra={
        <Button type='primary' onClick={() => handleEdit(true)}>
          Edit Recipe
        </Button>
      }
    >
      {recipe.image && (
        <Avatar
          src={recipe.image}
          alt={recipe.title}
          size={250}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Missing Products Warning */}
      <MissingProductsBanner
        missingProducts={missingProducts}
        venueId={venueId}
        onProductsAdded={onMissingProductsResolved}
        context='detail'
      />

      {/* Shared with missing ingredients warning */}
      {recipe?.needsIngredientReview &&
      recipe?.missingSharedIngredients?.length > 0 ? (
        <div
          style={{
            background: '#fff2e8',
            border: '1px solid #ffb366',
            borderRadius: 6,
            padding: 16,
            marginBottom: 16,
            color: '#d46b08',
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14 }}>
            ⚠ Needs Ingredient Review
          </div>
          <p style={{ margin: '0 0 6px', fontSize: 13 }}>
            This recipe was shared from another venue. Some ingredients could
            not be matched automatically. Please review and replace the missing
            ingredients before using this recipe.
          </p>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>
            Missing ingredients:
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
            {recipe.missingSharedIngredients.map((ing, idx) => (
              <li key={`${ing.title}-${idx}`}>
                {ing.title || 'Unknown'}
                {ing.sku ? ` (SKU: ${ing.sku})` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <RecipeGpReviewBanner recipe={recipe} onMarkReviewed={onMarkReviewed} />

      {displayEconomics.isStale ? (
        <RecipeEconomicsStaleHint
          lineSum={displayEconomics.lineSum}
          persistedCostPerRecipe={displayEconomics.costPerRecipe}
        />
      ) : null}

      <MonitoringNote data-testid='recipe-monitoring-detail'>
        {recipeMonitoringDetailCopy(recipe)}
      </MonitoringNote>

      <div>
        <p>{recipe.description}</p>
        <p>
          <strong>Category: &nbsp; </strong> {recipe.category?.name}
        </p>
        <div>
          <p>
            <strong>Cost Per Recipe: &nbsp; </strong>
            {formatGbp(displayEconomics.costPerRecipe)}
          </p>
          <p>
            <strong>Portions: &nbsp; </strong>
            {recipe.portions ?? 1}
          </p>
          <p>
            <strong>Cost Per Portion: &nbsp; </strong>
            {formatGbp(displayEconomics.costPerPortion)}
          </p>
          <p>
            <strong>Selling Price: &nbsp; </strong>
            {formatGbp(
              calculateSellingPriceInclVatFormatted(
                recipe?.sellingPrice,
                recipe?.hasVat,
              ) || recipe?.sellingPrice,
            )}
            {recipe?.hasVat ? (
              <span style={{ fontSize: 12, color: '#666', marginLeft: 6 }}>
                (incl. VAT)
              </span>
            ) : (
              <span style={{ fontSize: 12, color: '#666', marginLeft: 6 }}>
                (excl. VAT)
              </span>
            )}
          </p>
          {isRecipeArchived(recipe) ? null : (
            <p data-testid='recipe-detail-gp'>
              <strong>Gross Profit:</strong>{' '}
              <Tooltip title={GP_COLOUR_GUIDE_TEXT}>
                <InfoCircleOutlined
                  style={{ marginLeft: 4, marginRight: 6, color: '#8c8c8c' }}
                />
              </Tooltip>
              <RecipeGpBadge grossProfit={recipe.grossProfit} />
            </p>
          )}

          <h3>Ingredients</h3>
          <Table
            dataSource={ingredients}
            rowKey={(record, index) => record._id || record.id || index}
            columns={[
              {
                title: 'SKU',
                key: 'sku',
                render: (_, record) => record.product?.sku || '',
              },
              {
                title: 'Ingredient',
                dataIndex: ['product', 'title'],
                key: 'title',
              },
              {
                title: 'Quantity',
                dataIndex: 'size',
                key: 'size',
              },
              {
                title: 'UOM',
                key: 'UoM',
                render: (_, record) => record.UoM || record.product?.UoM || '',
              },
              {
                title: 'Price (£)',
                key: 'price',
                render: (_, record) => {
                  const { lineTotal, sizeLabel } =
                    computeIngredientTotal(record)

                  return (
                    <>
                      £{lineTotal.toFixed(2)} <br />
                      <small style={{ color: '#888' }}>{sizeLabel}</small>
                    </>
                  )
                },
              },
            ]}
            pagination={false}
          />

          <h3 style={{ marginTop: 16 }}>Recipe Ingredients</h3>
          {recipe?.recipeIngredients?.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recipe.recipeIngredients.map((ri, idx) => {
                const base = recipeById.get(ri.recipeId)
                const name = base?.title || ri.recipeName || 'Recipe'
                const isLocked = (() => {
                  const v = ri.isLocked
                  if (v === undefined || v === null) return true
                  if (typeof v === 'string') return v?.toLowerCase() === 'true'
                  if (typeof v === 'number') return v !== 0
                  return !!v
                })()
                const portions = ri.portions || 1
                const isLinked = ri?.isLinked !== false
                const costPerPortion = Number(
                  isLinked && base?.costPerPortion != null
                    ? base.costPerPortion
                    : ri.costPerPortion ?? base?.costPerPortion ?? 0,
                )
                const totalCost = costPerPortion * portions
                const allergens = base
                  ? getAllergensForBase(base)
                  : ri.allergens || []
                const key = ri.id || `${ri.recipeId}-${idx}`
                return (
                  <div
                    key={key}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: 6,
                      padding: 10,
                      background: isLocked ? '#f0f8ff' : '#fafafa',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 6,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span
                          style={{ cursor: base ? 'pointer' : 'default' }}
                          onClick={() =>
                            base &&
                            setExpandedStack(prev => ({
                              ...prev,
                              [key]: !prev[key],
                            }))
                          }
                        >
                          {expandedStack[key] ? (
                            <CaretDownOutlined />
                          ) : (
                            <CaretRightOutlined />
                          )}
                        </span>
                        <strong>{name}</strong>
                        <Tag color='blue'>📖 Recipe</Tag>
                        {isLocked ? (
                          <LockOutlined style={{ color: '#52c41a' }} />
                        ) : (
                          <UnlockOutlined style={{ color: '#faad14' }} />
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {portions} portion{portions !== 1 ? 's' : ''} | £
                        {Number(totalCost).toFixed(2)}
                      </div>
                    </div>
                    {allergens?.length ? (
                      <div
                        style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
                      >
                        {allergens.map(a => (
                          <Tag key={a} color='orange'>
                            {a}
                          </Tag>
                        ))}
                      </div>
                    ) : null}

                    {expandedStack[key] && (
                      <div style={{ marginTop: 10 }}>
                        <strong>Ingredient Breakdown</strong>
                        {base?.ingredients?.length ? (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 6,
                              marginTop: 6,
                            }}
                          >
                            {base.ingredients.map((ingredient, i) => {
                              const { lineTotal } =
                                computeIngredientTotal(ingredient)
                              return (
                                <div
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '6px 8px',
                                    background: '#f9f9f9',
                                    borderRadius: 4,
                                    fontSize: 12,
                                  }}
                                >
                                  <span>
                                    {ingredient.product?.title ||
                                      ingredient.title ||
                                      'Unknown Ingredient'}
                                  </span>
                                  <span>
                                    {ingredient.size} {ingredient.UoM} - £
                                    {(lineTotal || 0).toFixed(2)}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: 12,
                              color: '#999',
                              fontStyle: 'italic',
                              marginTop: 4,
                            }}
                          >
                            No ingredient details available
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p>No recipe ingredients.</p>
          )}

          <h3>Notes</h3>
          {recipe.notes ? (
            <>
              <p>{recipe.notes.text}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {recipe.notes.images.map((image, index) => (
                  <img
                    key={index}
                    src={image.signedURL}
                    alt={`note-image-${index}`}
                    width={100}
                    height={100}
                    style={{ borderRadius: '8px', cursor: 'pointer' }}
                    onClick={() => openImageModal(image.signedURL)}
                  />
                ))}
              </div>
            </>
          ) : (
            <p>No notes available.</p>
          )}

          <h3>Steps</h3>
          {recipe.steps && recipe.steps.length > 0 ? (
            <ul>
              {recipe.steps.map((step, index) => (
                <li
                  key={index}
                  style={{ marginBottom: '8px', listStyle: 'none' }}
                >
                  <Checkbox>{step}</Checkbox>
                </li>
              ))}
            </ul>
          ) : (
            <p>No steps available.</p>
          )}

          <h3>Allergens {allergensEditedByUser ? '(Edited By User)' : ''}</h3>
          {(() => {
            const allergens = getAllergens()
            const allergenTraces = calculatedAllergens.allergenTraces || []

            if (allergens.length === 0 && allergenTraces.length === 0) {
              return <p>No allergens listed</p>
            }

            return (
              <div>
                {/* Regular allergens */}
                {allergens.length > 0 && (
                  <div
                    style={{
                      marginBottom: allergenTraces.length > 0 ? '12px' : '0',
                    }}
                  >
                    {allergens.map((allergen, index) => (
                      <Tag key={`allergen-${index}`} color='red'>
                        {allergen}
                      </Tag>
                    ))}
                  </div>
                )}

                {/* Allergen traces */}
                {allergenTraces.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#666',
                        marginBottom: '4px',
                      }}
                    >
                      May Contain:
                    </div>
                    {allergenTraces.map((allergen, index) => (
                      <Tag key={`trace-${index}`} color='orange'>
                        {allergen}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          <StyledModal
            visible={isModalVisible}
            footer={null}
            onCancel={() => setIsModalVisible(false)}
            centered
          >
            <img
              src={selectedImage}
              alt='Enlarged Note'
              style={{ width: '100%' }}
            />
          </StyledModal>
        </div>
      </div>
    </StyledCard>
  )
}

export default RecipeDetails
